import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../../components/ui/select";

const asDisplay = (v) => (v === undefined || v === null ? "" : String(v));
const toNumOrUndef = (v) => (v === "" ? undefined : Number(v));
const digitsOnly = (s) => s.replace(/\D/g, ""); // allow only 0-9 in the box
const isNum = (n) => typeof n === "number" && Number.isFinite(n);


function formatNumber(value) {
  if (value == null || isNaN(value)) return "";
  return new Intl.NumberFormat("en-US").format(value);
}

function parseNumber(value) {
  const num = Number(value.replace(/,/g, ""));
  return isNaN(num) ? undefined : num;
}

export function RadiusSection() {
  const { control, setValue } = useFormContext();

  const r0 = useWatch({ control, name: "step8.radius0_50" });
  const r1 = useWatch({ control, name: "step8.radius51_200" });
  const r2 = useWatch({ control, name: "step8.radius201_500" });
  const r3 = useWatch({ control, name: "step8.radius501" });

  // remaining capacity for each bucket (for helper text only)
  const cap1 = Math.max(0, 100 - (isNum(r0) ? r0 : 0));
  const cap2 = Math.max(0, cap1 - (isNum(r1) ? r1 : 0));
  const cap3 = Math.max(0, cap2 - (isNum(r2) ? r2 : 0));
  const total = (isNum(r0) ? r0 : 0) + (isNum(r1) ? r1 : 0) + (isNum(r2) ? r2 : 0) + (isNum(r3) ? r3 : 0);

  // helper to wire inputs: keep raw empty as undefined; store numbers as Number
  const handlers = (path) => ({
    onChange: (raw) => {
      const cleaned = digitsOnly(raw);
      if (cleaned === "") setValue(path, undefined, { shouldDirty: true, shouldValidate: true });
      else setValue(path, Number(cleaned), { shouldDirty: true, shouldValidate: true });
    },
    onBlur: () => {
      // do nothing: no auto-clamp, no auto-fill — empty stays empty
    },
  });

  return (
    <section className="rounded-xl space-y-4">
      <h3 className="text-xl font-semibold">Radius</h3>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="rad-50">Under 50 miles</Label>
          <Controller
            control={control}
            name="step8.radius0_50"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="rad-50"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter..."
                  value={asDisplay(field.value)}
                  onChange={(e) => handlers("step8.radius0_50").onChange(e.target.value)}
                  onBlur={handlers("step8.radius0_50").onBlur}
                />
                {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                <p className="text-[11px] text-slate-500">Max 100%</p>
              </>
            )}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rad-51-200">51 - 200 Miles</Label>
          <Controller
            control={control}
            name="step8.radius51_200"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="rad-51-200"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter..."
                  value={asDisplay(field.value)}
                  onChange={(e) => handlers("step8.radius51_200").onChange(e.target.value)}
                  onBlur={handlers("step8.radius51_200").onBlur}
                />
                {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                <p className="text-[11px] text-slate-500">Max available: {cap1}%</p>
              </>
            )}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rad-201-500">201 - 500 Miles</Label>
          <Controller
            control={control}
            name="step8.radius201_500"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="rad-201-500"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter..."
                  value={asDisplay(field.value)}
                  onChange={(e) => handlers("step8.radius201_500").onChange(e.target.value)}
                  onBlur={handlers("step8.radius201_500").onBlur}
                />
                {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                <p className="text-[11px] text-slate-500">Max available: {cap2}%</p>
              </>
            )}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rad-500plus">500+ Miles</Label>
          <Controller
            control={control}
            name="step8.radius501"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="rad-500plus"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter..."
                  value={asDisplay(field.value)}
                  onChange={(e) => handlers("step8.radius501").onChange(e.target.value)}
                  onBlur={handlers("step8.radius501").onBlur}
                />
                {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
                <p className="text-[11px] text-slate-500">Max available: {cap3}%</p>
              </>
            )}
          />
        </div>
      </div>

      <p className={`text-sm ${total === 100 ? "text-green-700" : "text-red-700"}`}>
        Total: {Number.isFinite(total) ? total : 0}%
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Interstate or Intrastate?</Label>
          <Controller
            control={control}
            name="step8.stateOperation"
            render={({ field, fieldState }) => (
              <>
                <Select value={field.value ?? undefined} onValueChange={field.onChange} onOpenChange={() => field.onChange(field.value)}>
                  <SelectTrigger className="w-full !h-10 py-2 px-3"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="interstate">Interstate</SelectItem>
                    <SelectItem value="intrastate">Intrastate</SelectItem>
                  </SelectContent>
                </Select>
                {fieldState.error && <p className="text-xs text-red-600">{fieldState.error.message}</p>}
              </>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="annual-mileage">Annual Mileage</Label>
          <Controller
            control={control}
            name="step8.annualMileage"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="annual-mileage"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter..."
                  value={formatNumber(field.value)}
                  onChange={(e) => {
                    const input = e.target;
                    const raw = input.value;

                    // Save cursor position relative to digits
                    const prevCursor = input.selectionStart ?? raw.length;
                    const digitsBeforeCursor = raw.slice(0, prevCursor).replace(/,/g, "").length;

                    // Parse and update form state
                    const parsed = parseNumber(raw);
                    field.onChange(parsed);

                    // Reformat the string
                    const formatted = formatNumber(parsed);

                    // Next tick: restore caret after correct digit
                    requestAnimationFrame(() => {
                      let pos = 0, digitsSeen = 0;
                      while (digitsSeen < digitsBeforeCursor && pos < formatted.length) {
                        if (/\d/.test(formatted[pos])) digitsSeen++;
                        pos++;
                      }
                      input.setSelectionRange(pos, pos);
                    });
                  }}
                />
                {fieldState.error && (
                  <p className="text-xs text-red-600">
                    {fieldState.error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>
      </div>
    </section>
  );
}
